import { useEffect, useState } from "react";
import { SecurityBastionCard } from "@/components/SecurityBastionCard";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { fetchMyAnnonces, fetchMyFavorites, fetchConversations } from "@/lib/api";
import AccountSidebar from "@/components/AccountSidebar";
import PhoneOtpModal from "@/components/PhoneOtpModal";
import { FileText, Heart, MessageCircle, Plus, Bell, ShieldCheck, ShieldAlert, Crown } from "lucide-react";

export default function MonComptePage() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({ annonces: 0, favorites: 0, conversations: 0 });
  const [otpOpen, setOtpOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchMyAnnonces().catch(() => []),
      fetchMyFavorites().catch(() => []),
      fetchConversations().catch(() => []),
    ]).then(([a, f, c]) => setCounts({ annonces: a.length, favorites: f.length, conversations: c.length }));
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="mon-compte-page">
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Mon compte</h1>
      <p className="text-[var(--ink-soft)] mt-2">Bienvenue, <b>{user?.name || user?.email}</b>.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <AccountSidebar />

        <section>
          {user && (
            <div
              className={`mb-4 rounded-2xl p-4 flex flex-wrap items-center gap-3 border-2 ${
                user.phone_verified
                  ? "border-emerald-300 bg-emerald-50"
                  : "border-orange-300 bg-orange-50"
              }`}
              data-testid="phone-verify-banner"
            >
              {user.phone_verified ? (
                <>
                  <ShieldCheck className="text-emerald-600 shrink-0" size={22} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-emerald-700">Téléphone vérifié</div>
                    <p className="text-sm text-emerald-800/80">
                      Numéro confirmé : <b>{user.phone_e164 || user.phone}</b>. Vos annonces gagnent en confiance.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <ShieldAlert className="text-orange-600 shrink-0" size={22} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-orange-700">Vérifiez votre téléphone</div>
                    <p className="text-sm text-orange-800/80">
                      Obligatoire pour publier en Côte d'Ivoire (anti-fraude). Recevez un SMS gratuit en 30 s.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOtpOpen(true)}
                    className="btn-primary inline-flex items-center gap-2 shrink-0"
                    data-testid="phone-verify-cta"
                  >
                    <ShieldCheck size={14} /> Vérifier mon numéro
                  </button>
                </>
              )}
            </div>
          )}

          {/* Promotion Fodindin Pro pour les non-abonnés */}
          {user && !user.is_pro && (
            <Link
              to="/abonnements/pro"
              className="mb-4 rounded-2xl p-4 flex flex-wrap items-center gap-3 border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 hover:shadow-md transition-all"
              data-testid="pro-upsell-banner"
            >
              <Crown className="text-amber-600 shrink-0" size={22} />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-amber-700">Passez Pro pour 2 000 FCFA/mois</div>
                <p className="text-sm text-amber-800/80">
                  IA Booster, multipays, badge doré, auto-bump. Vendez 3x plus vite.
                </p>
              </div>
              <span className="text-xs font-bold text-amber-700 bg-amber-200 px-3 py-1 rounded-full shrink-0">
                Découvrir →
              </span>
            </Link>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatTile icon={FileText} label="Mes annonces" count={counts.annonces} to="/mes-annonces" testid="tile-annonces" />
            <StatTile icon={Heart} label="Favoris" count={counts.favorites} to="/favoris" testid="tile-favoris" />
            <StatTile icon={MessageCircle} label="Conversations" count={counts.conversations} to="/messages" testid="tile-messages" />
            <StatTile icon={Bell} label="Mes alertes" count="—" to="/mes-alertes" testid="tile-alertes" />
          </div>

          <div className="mt-6 glass-card p-7">
            <div className="font-bold text-lg mb-2">Profil</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div><span className="text-[var(--ink-soft)]">Nom : </span><b>{user?.name || "-"}</b></div>
              <div><span className="text-[var(--ink-soft)]">Email : </span><b>{user?.email}</b></div>
              <div><span className="text-[var(--ink-soft)]">Telephone : </span><b>{user?.phone || "-"}</b></div>
              <div><span className="text-[var(--ink-soft)]">Ville : </span><b>{user?.city || "-"}</b></div>
            </div>
            <Link to="/parametres" className="btn-secondary inline-flex mt-5">Modifier le profil</Link>
          </div>

          <SecurityBastionCard />

          <div className="mt-6 rounded-[24px] bg-gradient-to-br from-orange-500 to-orange-400 text-white p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-90">Astuce</div>
              <div className="text-xl font-extrabold mt-1">Boostez votre annonce a 2 000 FCFA</div>
              <div className="opacity-95 text-sm mt-1">Photos premium, badge verifie, mise en avant pendant 30 jours.</div>
            </div>
            <Link to="/deposer" className="btn-secondary inline-flex items-center gap-2"><Plus size={14} /> Deposer / Booster</Link>
          </div>
        </section>
      </div>

      <PhoneOtpModal open={otpOpen} onOpenChange={setOtpOpen} />
    </main>
  );
}

function StatTile({ icon: Icon, label, count, to, testid }) {
  return (
    <Link to={to} className="glass-card p-5 hover:translate-y-[-2px] transition-transform" data-testid={testid}>
      <div className="w-10 h-10 rounded-xl bg-orange-50 text-orange-600 grid place-items-center"><Icon size={18} /></div>
      <div className="mt-3 text-xs uppercase tracking-widest text-[var(--ink-soft)]">{label}</div>
      <div className="mt-1 text-2xl font-extrabold text-orange-600">{count}</div>
    </Link>
  );
}
